// Helpers for recovering a human-friendly original upload filename from the
// S3 object key (or a signed S3 URL) that a scanned document / transaction
// carries.
//
// Uploads are stored under `uploads/{uuidv4}_{originalFileName}` (see
// dialog-upload-statements.js). The transaction's `_awsUploadedDocumentKey`
// (and therefore the signed S3 URL path) preserves that encoding, so the
// original filename the user uploaded is recoverable without a backend round
// trip. Deriving it here keeps the PDF-download prompt showing the real
// filename + a `.pdf` type instead of a chunk of the signed URL/query string.

// UUID v4 prefix inserted at upload time: `{uuid}_`.
const UPLOAD_UUID_PREFIX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i;

// Take the final path segment of an S3 key or URL pathname, tolerating both
// forward and back slashes.
const basename = (path) => {
    if (!path) return '';
    const parts = String(path).split(/[/\\]/);
    return parts[parts.length - 1] || '';
};

const safeDecode = (value) => {
    try {
        return decodeURIComponent(value);
    } catch {
        // Malformed percent-encoding — fall back to the raw value.
        return value;
    }
};

/**
 * Ensure a filename ends with a `.pdf` extension (case-insensitive).
 * @param {string} name
 * @returns {string}
 */
export const ensurePdfExtension = (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return 'document.pdf';
    return /\.pdf$/i.test(trimmed) ? trimmed : `${trimmed}.pdf`;
};

/**
 * Recover the original upload filename from an S3 object key.
 * Strips the folder path and the `{uuid}_` prefix added at upload time.
 * @param {string} key - e.g. `uploads/2b3c...-..._March Statement.pdf`
 * @returns {string|null} the original filename, or null if not derivable
 */
export const originalFilenameFromKey = (key) => {
    const name = safeDecode(basename(key));
    if (!name) return null;
    return name.replace(UPLOAD_UUID_PREFIX, '') || name;
};

/**
 * Recover the original upload filename from a (signed) S3 URL by parsing its
 * path — the query string (signature params) is discarded.
 * @param {string} url
 * @returns {string|null} the original filename, or null if not derivable
 */
export const originalFilenameFromUrl = (url) => {
    if (!url) return null;
    let pathname;
    try {
        pathname = new URL(url).pathname;
    } catch {
        // Not an absolute URL — strip a query string manually and treat as a path.
        pathname = String(url).split('?')[0];
    }
    return originalFilenameFromKey(pathname);
};
